import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Newspaper, Video, Handshake, GraduationCap, Play, Loader2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';

const HERMES_EASE = [0.22, 1, 0.36, 1] as const;

interface MediaItem {
  id: string;
  type: 'media' | 'video' | 'brand' | 'graduate';
  typeLabel: string;
  title: string;
  description: string;
  imageUrl?: string;
  videoUrl?: string;
  link: string;
  linkText: string;
  date?: string;
  isActive?: boolean;
  sortOrder?: number;
}

function getTypeIcon(type: MediaItem['type']) {
  switch (type) {
    case 'video':
      return Video;
    case 'brand':
      return Handshake;
    case 'graduate':
      return GraduationCap;
    case 'media':
    default:
      return Newspaper;
  }
}

function MediaCard({ item, index }: { item: MediaItem; index: number }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const Icon = getTypeIcon(item.type);

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col h-full border border-orange-100 hover:shadow-xl transition-shadow duration-300"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.7, ease: HERMES_EASE, delay: index * 0.12 }}
    >
      {/* Image / Video Area */}
      <div className="h-56 overflow-hidden bg-gray-100 relative group">
        {item.videoUrl && !videoError ? (
          <iframe
            width="100%"
            height="100%"
            src={item.videoUrl}
            title={item.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            onError={() => setVideoError(true)}
          />
        ) : item.imageUrl ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}
            <img
              src={item.imageUrl}
              alt={item.title}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              loading="lazy"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-orange-50">
            <Play className="w-12 h-12 text-orange-300" />
          </div>
        )}

        {/* Type badge */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm">
          <Icon className="w-3.5 h-3.5 text-orange-600" />
          <span className="text-xs font-bold text-orange-700">{item.typeLabel}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-bold text-gray-900 mb-2 leading-snug">{item.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed flex-1 mb-4">{item.description}</p>

        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
          {item.date && (
            <span className="text-xs text-gray-400">{item.date}</span>
          )}
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-bold text-orange-600 hover:text-orange-700 transition-colors ml-auto"
          >
            {item.linkText}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function MediaCoverage() {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadMedia() {
      setLoading(true);
      setError(null);

      const res = await apiFetch<MediaItem[]>('/api/public/media-coverage');

      if (cancelled) return;

      if (!res.success || !res.data) {
        setError(res.error?.message ?? '載入失敗，請稍後重試');
        setMediaItems([]);
        setLoading(false);
        return;
      }

      const activeItems = res.data
        .filter((item) => item.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

      setMediaItems(activeItems);
      setLoading(false);
    }

    loadMedia();

    apiFetch<Record<string, string>>('/api/public/settings').then((res) => {
      if (res.success && res.data) setSettings(res.data);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="section-padding bg-gradient-to-b from-white to-orange-50/50">
      <div className="container-max mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: HERMES_EASE }}
        >
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
            <Newspaper className="w-4 h-4" />
            {settings.mediaBadge || '社會認同'}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            {settings.mediaTitle || '媒體報導與合作夥伴'}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {settings.mediaDescription || '好餸傳愛的社區飯堂模式獲得媒體關注與企業支持，共同推動 SEN 青年的共融職訓發展'}
          </p>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
            <p className="text-gray-500 text-sm">載入中...</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-red-500 text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition-colors"
            >
              重新載入
            </button>
          </motion.div>
        )}

        {/* Grid */}
        {!loading && !error && mediaItems.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {mediaItems.map((item, index) => (
              <MediaCard key={item.id} item={item} index={index} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && mediaItems.length === 0 && (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-gray-500 text-sm">暫無內容</p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
